/**
 * Microsoft Clarity:
 * - With analytics consent only: marketing + /dashboard/from-file; stop elsewhere.
 * - With explicit sessionReplay consent: all paths (including CV surfaces).
 */

import {
  hasAnalyticsConsent,
  hasSessionReplayConsent,
  isPublicAnalyticsPath,
} from './cookieConsent';

/** Choice screen only — no CV content on screen (marketing-scope recording). */
export const CLARITY_FROM_FILE_PATH = '/dashboard/from-file';

/**
 * Paths where Clarity may record without full session-replay consent.
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export const isClarityAnalyticsPath = (pathname) => {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  if (path === CLARITY_FROM_FILE_PATH) return true;
  return isPublicAnalyticsPath(pathname);
};

let injected = false;
let stopped = false;

/**
 * Production host only. Never localhost or preview deploys.
 *
 * @param {string} [hostname]
 * @returns {boolean}
 */
export const isClarityHost = (hostname) => {
  const host =
    hostname || (typeof window !== 'undefined' ? window.location.hostname : '');
  return host === 'clarcv.com' || host === 'www.clarcv.com';
};

const callClarity = (command, ...args) => {
  if (typeof window === 'undefined' || typeof window.clarity !== 'function') {
    return;
  }
  try {
    window.clarity(command, ...args);
  } catch (err) {
    // Tag may not expose the command until it loads; the stub queues arguments.
  }
};

/**
 * Official Clarity snippet. Consent + scope tag after Accept.
 *
 * @param {string} projectId
 * @param {'full'|'marketing'} scope
 */
const injectClarity = (projectId, scope) => {
  window.clarity =
    window.clarity ||
    function clarityStub() {
      // eslint-disable-next-line prefer-rest-params
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`;
  const first = document.getElementsByTagName('script')[0];
  if (first && first.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }

  window.clarity('consent');
  window.clarity('set', 'replay_scope', scope);
  injected = true;
  stopped = false;
};

const resumeClarity = () => {
  if (!injected || !stopped) return;
  callClarity('start');
  stopped = false;
};

const stopClarity = () => {
  if (!injected || stopped) return;
  callClarity('stop');
  stopped = true;
};

/**
 * Align Clarity with the current SPA path and consent level.
 *
 * @param {string} pathname
 * @param {{ projectId?: string, hostname?: string }} [opts]
 */
export const syncClarityForPath = (pathname, opts = {}) => {
  const projectId = (opts.projectId || '').trim();
  if (!projectId || !isClarityHost(opts.hostname)) {
    return;
  }

  const fullReplay = hasSessionReplayConsent();
  const analyticsOnly = hasAnalyticsConsent() && !fullReplay;

  if (!fullReplay && !analyticsOnly) {
    return;
  }

  if (fullReplay) {
    if (!injected) {
      injectClarity(projectId, 'full');
      return;
    }
    resumeClarity();
    callClarity('set', 'replay_scope', 'full');
    return;
  }

  // Analytics consent only: public + from-file.
  if (isClarityAnalyticsPath(pathname)) {
    if (!injected) {
      injectClarity(projectId, 'marketing');
      return;
    }
    resumeClarity();
    return;
  }
  stopClarity();
};

/**
 * Reset inject/stop flags (unit tests only).
 */
export const resetClarityState = () => {
  injected = false;
  stopped = false;
};
